/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./PopupDialog.css";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";

import { getConfig } from "../config";

export default function PopupDialog({
  openErrorPopup,
  errorMessage,
  headerMessage,
  componentName,
  onClose,
  handleDeleteApp,
  handleAddEditTeam,
  handleDeleteTeam,
  popupData,
  type,
}) {
  const [userEmailId, setUserEmailId] = useState("");
  const [role, setRole] = useState("MANAGER");
  const teamDataRef = useRef(popupData);
  const [inputErrMsg, setInputErrMsg] = useState("");

  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const invalidUserMessageRegex=/^[a-zA-Z0-9-]+ is trying to login to [a-zA-Z0-9-]+ portal$/;

  const handleClose = () => {
    if( componentName === "appError" && errorMessage?.toLowerCase().includes("jwt expired")) {
      handleLogout();
    }
    if (componentName === "teams") {
      setUserEmailId("");
      setRole("MANAGER");
    }

    if (componentName === "editTeamMember") {
      setUserEmailId(popupData.email);
      setRole(popupData.role);
    }

    setInputErrMsg("");
    onClose();
  };

  const handleLogout = () => {
    const app_portal = getConfig().REACT_APP_PORTAL;
    const clientId = getConfig().REACT_APP_CLIENT_ID;
    const scopeId =
      "rs%2FigniteStoreSeller%20openid%20aws.cognito.signin.user.admin";
    if (
      sessionStorage.getItem("idpLogin") &&
      sessionStorage.getItem("idpLogin") === "false"
    ) {
      const cognitoDomain = getConfig().REACT_APP_COGNITO_DOMAIN;
      const redirect_uri = getConfig().REACT_APP_REDIRECT_URI;
      const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&response_type=code&scope=${scopeId}&redirect_uri=${redirect_uri}&portal=${app_portal}`;
      sessionStorage.clear();
      globalThis.location.href = logoutUrl;
    } else {
      const redirect_uri = getConfig().REACT_APP_REDIRECT_URI;
      let logoutUrl = `${
        getConfig().REACT_APP_VW_LOGOUT_URL
      }${ sessionStorage.getItem(
        "id_token"
      )}&post_logout_redirect_uri=${redirect_uri}&state=logout_state_1`;
      sessionStorage.clear();
      globalThis.location.href = logoutUrl;
    }
  };

  const handleDelete = () => {
    if (componentName !== "teams" && componentName !== "deleteTeamMember") {
      handleDeleteApp();
    } else {
      handleDeleteTeam({
        id: popupData.id,
        email: popupData.email,
        role: popupData.role,
      });
    }
  };

  const handleAddEditTeamAction = () => {
    if (componentName === "editTeamMember") {
      if (userEmailId === "") {
        setUserEmailId(popupData.email);
      }
      handleAddEditTeam({
        email: userEmailId,
        role: role.toUpperCase(),
        id: popupData.id,
      });
    }
    if (componentName === "teams") {
      handleAddEditTeam({
        email: userEmailId,
        role: role,
      });
    }
  };

  const validateInput = (value) => {
    if (!value) {
      setInputErrMsg("User email is required");
      return false;
    }
    if (!emailRegex.test(value)) {
      setInputErrMsg("Please enter a valid email address");
      return false;
    }
    setInputErrMsg("");
    return true;
  };

  const handleUserEmailChange = (event) => {
    setUserEmailId(event.target.value);
    validateInput(event.target.value);
  };

  const handleRoleChange = (event) => {
    setRole(event.target.value);
  };

  useEffect(() => {
    if (componentName === "editTeamMember") {
      setUserEmailId(popupData.email);
      setRole(popupData.role);
    }
    if (componentName === "teams") {
      setUserEmailId(popupData.email);
      setRole(popupData.role);
    }
  }, [teamDataRef, popupData]);

  return (
    <Dialog
      open={openErrorPopup}
      onClose={errorMessage === "jwt expired" ? handleLogout : handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      {(componentName === "teams" || componentName === "editTeamMember") && (
        <DialogTitle id="alert-dialog-title">
          <div className="teamHeaderCls">
            <span>{headerMessage}</span>
            {componentName === "teams" && (
              <span className="subHeaderCls">
                Add team members associated with your app listing.
              </span>
            )}
            {componentName === "editTeamMember" && (
              <span className="subHeaderCls">
                Edit team members associated with your app listing.
              </span>
            )}
          </div>
        </DialogTitle>
      )}

      {(componentName === "appError" ||
        componentName === "userAuthFail" ||
        componentName === "deleteError" ||
        componentName === "deleteTeamMember") && (
        <DialogTitle style={{ color: "#da263f" }} id="alert-dialog-title">
          {headerMessage}
        </DialogTitle>
      )}

      {(componentName === "userAuthFail" ||
        componentName === "appError" ||
        componentName === "deleteError") && (
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {errorMessage}
          </DialogContentText>
        </DialogContent>
      )}

      {(componentName === "teams" || componentName === "editTeamMember") && (
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <div style={{ color: "black" }}>
              <label className="teamFormLabel" htmlFor="userEmailInput">
                User Email ID <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                type="email"
                autoComplete="off"
                value={userEmailId}
                style={{
                  marginBottom: "0",
                  marginLeft: "0",
                  borderColor: inputErrMsg ? "red" : "black",
                }}
                fullWidth
                id="userEmailInput"
                onChange={handleUserEmailChange}
              />
            </div>
            {inputErrMsg && (
              <span style={{ color: "red", fontSize: "12px" }}>
                {inputErrMsg}
              </span>
            )}
            &nbsp;
            <div>
              <FormControl>
                <span className="teamFormLabel" id="role-radio-group-label">
                  Select Role <span style={{ color: "red" }}>*</span>
                </span>
                <RadioGroup
                  row
                  aria-labelledby="role-radio-group-label"
                  defaultValue={popupData.role.toLocaleUpperCase()}
                  name="radio-buttons-group"
                  onChange={handleRoleChange}
                >
                  <FormControlLabel
                    sx={{
                      border: "1px solid #12141533",
                      height: "35px!important",
                      paddingRight: "20px",
                      borderRadius: "4px",
                      marginLeft: "0px",
                    }}
                    value="MANAGER"
                    control={<Radio size="small" />}
                    label="Manager"
                  />
                  <FormControlLabel
                    sx={{
                      border: "1px solid #12141533",
                      height: "35px!important",
                      paddingRight: "20px",
                      borderRadius: "4px",
                      marginLeft: "0px",
                    }}
                    value="MEMBER"
                    control={<Radio size="small" />}
                    label="Member"
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </DialogContentText>
        </DialogContent>
      )}

      {componentName === "deleteTeamMember" && (
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {errorMessage}
          </DialogContentText>
        </DialogContent>
      )}

      {componentName === "delete" && (
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <div style={{ color: "black" }}>
              <p>
                You are about to delete the {type} listing and all its data
                (e.g. {type === "application" ? "APK files," : ""} statistics,
                etc.).
              </p>
              <p>
                The {type} will be removed from the Ignite Store catalog, but
                remains installed in the users system.
              </p>
              <p>That action can not be undone.</p>
            </div>
          </DialogContentText>
        </DialogContent>
      )}

      {componentName === "userAuthFail" ||
        ((errorMessage === "jwt expired" || invalidUserMessageRegex.test(errorMessage)) && (
          <DialogActions>
            <Button className="btnTextCls" onClick={handleLogout}>
              Logout
            </Button>
          </DialogActions>
        ))}
      {componentName === "delete" && (
        <DialogActions>
          <Button className="btnTextCls" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className="btnTextCls"
            style={{ background: "#da263f", color: "white" }}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </DialogActions>
      )}
      {componentName === "deleteTeamMember" && (
        <DialogActions>
          <Button className="btnTextCls" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className="btnTextCls"
            style={{ background: "#da263f", color: "white" }}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </DialogActions>
      )}
      {componentName === "deleteError" && (
        <DialogActions>
          <Button className="btnTextCls" onClick={handleClose}>
            Ok
          </Button>
        </DialogActions>
      )}
      {componentName === "appError" && errorMessage !== "jwt expired" && (
        <DialogActions>
          <Button className="btnTextCls" onClick={handleClose}>
            Ok
          </Button>
        </DialogActions>
      )}

      {(componentName === "teams" || componentName === "editTeamMember") && (
        <DialogActions>
          <Button className="btnTextCls" onClick={handleClose}>
            Cancel
          </Button>

          {componentName === "teams" && (
            <Button
              variant="contained"
              disabled={
                !(userEmailId.length > 0 && inputErrMsg === "")
              }
              className="btnTextCls"
              onClick={handleAddEditTeamAction}
            >
              <span>Add</span>
            </Button>
          )}

          {componentName === "editTeamMember" && (
            <Button
              variant="contained"
              disabled={inputErrMsg !== ""}
              className="btnTextCls"
              onClick={handleAddEditTeamAction}
            >
              <span>Save</span>
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

PopupDialog.propTypes = {
  openErrorPopup: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  headerMessage: PropTypes.string,
  componentName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  handleDeleteApp: PropTypes.func,
  handleAddEditTeam: PropTypes.func,
  handleDeleteTeam: PropTypes.func,
  popupData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    role: PropTypes.string,
  }),
  type: PropTypes.string,
};
